import { useMemo, useContext } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { AmplitudeContext } from '@homebot/shared-util-quartermaster'

// Selectors
import {
  selectDisplayYlopoSpecificContent,
  selectListingsSourceIsHomebotListings,
  selectFavoriteListingIds,
} from '../store/selectors/listings'
import { selectCustomerId } from '../store/selectors/customerProfile'
import { selectClientId, selectClientIsGuest } from '../store/selectors/client'

import { toggleAuthModal } from '../actions/homeSearchAuthModal'
import { toggleFavoriteListing } from '../actions/favoriteListings'

// Helpers
import {
  buildViewAllSearchUrl as buildYlopoViewAllUrl,
  buildListingUrl as buildYlopoListingShowUrl,
  buildSingleZipSearchUrl as buildYlopoZipSearchUrl,
  buildMultiZipSearchUrl as buildYlopoMultiZipUrl,
} from '../helpers/ylopo'

import { PATH_HELPERS } from '../constants/navigation'
import isEmpty from 'lodash/isEmpty'
import { POSTAL_CODE } from '../constants/listings'

// Constructs the url that will be navigated to when the user clicks on a listing
// in the carousel
const useViewAllListingsUrl = searchParams => {
  // Ylopo listing source, route to Ylopo listing URLs
  const useYlopoListings = useSelector(selectDisplayYlopoSpecificContent)
  // Homebot listing source, route to homebot listing URLs
  const useHomebotListings = useSelector(selectListingsSourceIsHomebotListings)
  const customerProfileId = useSelector(selectCustomerId)
  const clientId = useSelector(selectClientId)

  if (useHomebotListings) {
    if (searchParams.zipcode) {
      return PATH_HELPERS.listings.buildPath(
        customerProfileId,
        POSTAL_CODE,
        searchParams.zipcode
      )
    }

    return PATH_HELPERS.listingsIndex.buildPath(customerProfileId)
  }
  if (useYlopoListings) {
    if (searchParams.zipcode) {
      return buildYlopoZipSearchUrl(clientId, searchParams)
    }

    if (searchParams.markets) {
      return buildYlopoMultiZipUrl(clientId, searchParams)
    }

    return buildYlopoViewAllUrl(clientId, searchParams)
  }
  return ''
}

const useViewListingShowPageUrl = listing => {
  const useYlopoListings = useSelector(selectDisplayYlopoSpecificContent)
  const useHomebotListings = useSelector(selectListingsSourceIsHomebotListings)
  const customerProfileId = useSelector(selectCustomerId)
  const clientId = useSelector(selectClientId)

  if (useHomebotListings) {
    return PATH_HELPERS.listing.buildPath(customerProfileId, listing.id)
  }

  if (useYlopoListings) {
    return buildYlopoListingShowUrl(clientId, listing)
  }
  return ''
}

const useIsFavoritedListing = listing => {
  const favoriteListingIds = useSelector(selectFavoriteListingIds)

  return favoriteListingIds && favoriteListingIds.includes(listing.id)
}

const useFavoriteListingClickHandler = () => {
  const clientIsGuest = useSelector(selectClientIsGuest)
  const dispatch = useDispatch()
  const customerId = useSelector(selectCustomerId)
  const { ampli } = useContext(AmplitudeContext)

  const handleClick = (e, listing) => {
    e.preventDefault()
    if (clientIsGuest) {
      dispatch(toggleAuthModal(true))
    } else {
      dispatch(toggleFavoriteListing({ listing, customerId, ampli }))
    }
  }

  return handleClick
}

const useFormattedListingAddress = listing => {
  const firstLineAddress = useMemo(() => {
    if (isEmpty(listing.address)) {
      return ''
    }

    const { number, street, unitNumber } = listing.address

    let address = ''

    if (number) {
      address += `${number} `
    }

    address += street

    if (unitNumber) {
      address += ` #${unitNumber}`
    }

    return address
  }, [listing])

  const secondLineAddress = useMemo(() => {
    if (isEmpty(listing.address)) {
      return ''
    }

    const { city, stateOrProvince, postalCode } = listing.address
    return `${city}, ${stateOrProvince} ${postalCode}`
  }, [listing])

  const fullAddress = useMemo(() => {
    if (isEmpty(firstLineAddress) || isEmpty(secondLineAddress)) {
      return ''
    }

    return `${firstLineAddress} ${secondLineAddress}`
  }, [firstLineAddress, secondLineAddress])

  return {
    fullAddress,
    firstLineAddress,
    secondLineAddress,
  }
}

export {
  useViewAllListingsUrl,
  useViewListingShowPageUrl,
  useIsFavoritedListing,
  useFavoriteListingClickHandler,
  useFormattedListingAddress,
}
